import { useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
    House,
    Ticket,
    Tag,
    Trophy,
    Newspaper,
    EnvelopeSimple,
    Images,
    Confetti,
    List,
    X,
    ArrowSquareOut,
    FacebookLogo,
    InstagramLogo,
    TennisBall,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const NAV = [
    { to: "/", label: "Accueil", icon: House, end: true, id: "accueil" },
    { to: "/actualites", label: "Actualités", icon: Newspaper, id: "actualites" },
    { to: "/inscriptions", label: "Inscriptions", icon: Ticket, id: "inscriptions" },
    { to: "/offres", label: "Offres", icon: Tag, id: "offres" },
    { to: "/competitions", label: "Compétitions", icon: Trophy, id: "competitions" },
    { to: "/evenements", label: "Événements", icon: Confetti, id: "evenements" },
    { to: "/galerie", label: "Galerie", icon: Images, id: "galerie" },
    { to: "/contact", label: "Contact", icon: EnvelopeSimple, id: "contact" },
];

const BOTTOM_NAV_IDS = ["accueil", "inscriptions", "offres", "competitions", "galerie"];

function useSettings() {
    return useQuery({
        queryKey: ["settings"],
        queryFn: async () => (await api.get("/settings")).data,
    });
}

function Header({ settings }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <header
                data-testid="site-header"
                className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70 saturate-150"
            >
                <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
                        <img src="/logo.png" alt="Logo Tennis Club La Turballe" className="h-11 w-11 md:h-14 md:w-14 object-contain" />
                        <div className="leading-none hidden sm:block">
                            <div className="font-display font-extrabold text-navy text-lg md:text-xl tracking-tight">
                                TC La Turballe
                            </div>
                            <div className="overline text-clay mt-1">Tennis · Pickleball</div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={`nav-${n.id}-link`}
                                className={({ isActive }) =>
                                    `px-3.5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                                        isActive ? "bg-navy text-white" : "text-slate-600 hover:text-navy hover:bg-slate-100"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href={settings?.tenup_url || "https://tenup.fft.fr"}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="tenup-header-btn"
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-clay px-4 py-2.5 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-clay-dark"
                        >
                            Ten'Up <ArrowSquareOut weight="bold" size={16} />
                        </a>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="mobile-menu-open"
                            className="lg:hidden grid place-items-center h-11 w-11 rounded-full border border-slate-200 text-navy"
                            aria-label="Ouvrir le menu"
                        >
                            <List weight="bold" size={22} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-navy-deep text-white flex flex-col"
                        data-testid="mobile-menu-overlay"
                    >
                        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <img src="/logo.png" alt="TCLT" className="h-10 w-10 object-contain" />
                                <span className="font-display font-extrabold text-lg">TC La Turballe</span>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="mobile-menu-close"
                                className="grid place-items-center h-11 w-11 rounded-full bg-white/10"
                                aria-label="Fermer le menu"
                            >
                                <X weight="bold" size={22} />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-6 gap-1">
                            {NAV.map((n, i) => (
                                <motion.div
                                    key={n.to}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={n.to}
                                        end={n.end}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-nav-${n.id}-link`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-4 py-4 border-b border-white/10 text-2xl font-display font-bold ${
                                                isActive ? "text-clay" : "text-white"
                                            }`
                                        }
                                    >
                                        <n.icon weight="duotone" size={28} />
                                        {n.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="p-6">
                            <a
                                href={settings?.tenup_url || "https://tenup.fft.fr"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 rounded-full bg-clay px-4 py-4 text-base font-bold"
                            >
                                Réserver sur Ten'Up <ArrowSquareOut weight="bold" size={18} />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function Footer({ settings }) {
    return (
        <footer className="mt-20 bg-navy-deep text-white noise" data-testid="site-footer">
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 grid gap-10 md:grid-cols-3">
                <div>
                    <div className="flex items-center gap-3 mb-4">
                        <img src="/logo.png" alt="TCLT" className="h-12 w-12 object-contain" />
                        <span className="font-display font-extrabold text-xl">TC La Turballe</span>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed max-w-xs">
                        {settings?.intro || "Club de tennis et de padel au bord de l'océan, sur la côte guérandaise."}
                    </p>
                </div>
                <div>
                    <div className="overline text-clay mb-4">Contact</div>
                    <ul className="space-y-2 text-sm text-slate-300">
                        {settings?.address && <li>{settings.address}</li>}
                        {settings?.phone && <li>{settings.phone}</li>}
                        {settings?.email && (
                            <li>
                                <a href={`mailto:${settings.email}`} className="hover:text-clay">{settings.email}</a>
                            </li>
                        )}
                        {settings?.hours && <li className="text-slate-400">{settings.hours}</li>}
                    </ul>
                </div>
                <div>
                    <div className="overline text-clay mb-4">Suivez-nous</div>
                    <div className="flex items-center gap-3">
                        {settings?.facebook_url && (
                            <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="grid place-items-center h-11 w-11 rounded-full bg-white/10 hover:bg-clay transition-colors">
                                <FacebookLogo weight="fill" size={22} />
                            </a>
                        )}
                        {settings?.instagram_url && (
                            <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="grid place-items-center h-11 w-11 rounded-full bg-white/10 hover:bg-clay transition-colors">
                                <InstagramLogo weight="fill" size={22} />
                            </a>
                        )}
                        <a href={settings?.tenup_url || "https://tenup.fft.fr"} target="_blank" rel="noopener noreferrer" data-testid="footer-tenup" className="inline-flex items-center gap-2 rounded-full bg-clay px-4 h-11 text-sm font-bold hover:bg-clay-dark transition-colors">
                            <TennisBall weight="fill" size={18} /> Ten'Up
                        </a>
                    </div>
                    <Link to="/admin/login" data-testid="footer-admin-link" className="inline-block mt-8 text-xs text-slate-500 hover:text-slate-300 font-mono uppercase tracking-widest">
                        Espace Admin
                    </Link>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 text-xs text-slate-400 flex flex-col sm:flex-row justify-between gap-2">
                    <span>© {new Date().getFullYear()} Tennis Club La Turballe — TCLT</span>
                    <span className="font-mono">{settings?.season_title}</span>
                </div>
            </div>
        </footer>
    );
}

function BottomNav() {
    const { pathname } = useLocation();
    return (
        <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 pb-safe">
            <div className="grid grid-cols-5">
                {NAV.filter((n) => BOTTOM_NAV_IDS.includes(n.id)).map((n) => {
                    const active = n.end ? pathname === n.to : pathname.startsWith(n.to);
                    return (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`bottomnav-${n.id}`}
                            className="flex flex-col items-center gap-1 py-2.5"
                        >
                            <n.icon weight={active ? "fill" : "regular"} size={22} className={active ? "text-clay" : "text-slate-500"} />
                            <span className={`text-[10px] font-semibold ${active ? "text-clay" : "text-slate-500"}`}>{n.label}</span>
                        </NavLink>
                    );
                })}
            </div>
        </nav>
    );
}

export default function Layout() {
    const { data: settings } = useSettings();
    return (
        <div className="min-h-screen flex flex-col">
            <Header settings={settings} />
            <main className="flex-1 pb-24 lg:pb-0">
                <Outlet />
            </main>
            <Footer settings={settings} />
            <BottomNav />
        </div>
    );
}
